import { listProductConfigs } from '@/app/config/products.config';
import { useRequirePermissions } from '@/shared/hooks';
import { formatDateTimeShortToBrazil } from '@/shared/utils/format-date.util';
import { useMemo } from 'react';
import { gatewayStatusOf } from '../utils/payment-settings-list.util';
import { useProductPaymentSettings } from './use-product-payment-settings.hook';

/** Editor de um produto: dados, nome, etiqueta de status e permissão de escrita. */
export function usePaymentSettingsEditor(productCode: string) {
  const {
    data: settings,
    isLoading,
    error,
    refetch,
  } = useProductPaymentSettings(productCode);

  // Espelha o guard da API: escrita exige `products.catalog.write` (a rota já
  // garante o grupo Administrador).
  const { hasPermission } = useRequirePermissions({
    permissions: ['products.catalog.write'],
  });

  const productName = useMemo(
    () =>
      listProductConfigs().find(config => config.productCode === productCode)
        ?.displayName ??
      settings?.productLabel ??
      productCode,
    [productCode, settings?.productLabel]
  );

  const status = settings
    ? gatewayStatusOf({
        gatewaySupported: settings.gatewaySupported,
        gatewayActive: settings.gateway.active,
        gatewayEnabled: settings.gateway.enabled,
      })
    : undefined;

  const updatedAt = formatDateTimeShortToBrazil(settings?.gateway.updatedAt);
  const updatedNote =
    settings?.gateway.updatedByName && updatedAt
      ? `Cobrança alterada por ${settings.gateway.updatedByName} em ${updatedAt}`
      : undefined;

  return {
    settings,
    isLoading,
    error:
      error instanceof Error
        ? error.message
        : error
          ? 'Não foi possível carregar o produto.'
          : null,
    refetch,
    productName,
    canEdit: hasPermission === true,
    status,
    updatedNote,
  };
}
